import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { finalize, takeUntil } from 'rxjs/operators';
import { TotpSetupResponse } from 'src/app/models/mfa/mfa.model';
import { MfaSetupService } from 'src/app/services/general/mfa-setup.service';
import { MetaService } from 'src/app/services/meta.service';
import { ComponentDestroyClass } from 'src/app/shared/classes/component-destroy.class';
import {
  customPatternValidator,
  customRequiredValidator,
} from 'src/app/shared/utils/form-validators';
import { AccessRulesService } from '../../services/access-rules.service';

/**
 * Required MFA setup: login answered `requireTotpGeneration: true` (TOTP enabled, user hasn't set it up).
 * QR → code → the setup session ends and the user signs in again with the authenticator code.
 */
@Component({
  standalone: false,
  selector: 'cms-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrls: ['./mfa-setup.component.scss'],
})
export class MfaSetupComponent extends ComponentDestroyClass implements OnInit, OnDestroy {
  confirmForm: FormGroup;

  infoLoading = false;

  confirmLoading = false;

  setup: TotpSetupResponse = null;

  private readonly REQUIRED_MESSAGE = 'The field should be filled in';

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private metaSvc: MetaService,
    private notificationSvc: NotificationsService,
    private mfaSetupSvc: MfaSetupService,
    private accessRulesService: AccessRulesService,
    private sanitizer: DomSanitizer,
  ) {
    super();
  }

  ngOnInit(): void {
    this.metaSvc.setTitle('Set Up Two-Factor Authentication');

    // Opened directly (e.g. page reload) — the setup token is gone, sign in again.
    if (!this.mfaSetupSvc.hasSetupToken) {
      this.router.navigate(['login']);
      return;
    }

    this.confirmForm = this.fb.group({
      code: [
        '',
        [
          customRequiredValidator(this.REQUIRED_MESSAGE),
          customPatternValidator(/^\d{6}$/, 'Code must contain 6 digits'),
        ],
      ],
    });

    this.loadInfo();
  }

  ngOnDestroy() {
    super.onDestroy();
    this.mfaSetupSvc.clear();
  }

  get logo(): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(this.accessRulesService.getLogoBase64());
  }

  get codeControl() {
    return this.confirmForm.get('code') as FormControl;
  }

  get qrCode() {
    return `data:image/png;base64,${this.setup.qrCodeBase64}`;
  }

  get secretKey() {
    return this.setup.key.match(/.{1,4}/g).join(' ');
  }

  get confirmBtnDisabled() {
    return this.confirmLoading || !this.setup || this.confirmForm.invalid;
  }

  onConfirm() {
    if (this.confirmBtnDisabled) {
      return;
    }

    this.confirmLoading = true;

    this.mfaSetupSvc
      .validate(String(this.codeControl.value).trim())
      .pipe(
        finalize(() => {
          this.confirmLoading = false;
        }),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe({
        next: () => {
          this.notificationSvc.success(
            'Success',
            'Two-Factor Authentication has been set up. Please sign in with your authenticator code.',
          );
          this.finishSetup();
        },
        error: (err: any) => {
          this.notificationSvc.error('Error', err.message);
        },
      });
  }

  onBackToLogin() {
    this.finishSetup();
  }

  private loadInfo() {
    this.infoLoading = true;

    this.mfaSetupSvc
      .getInfo()
      .pipe(
        finalize(() => {
          this.infoLoading = false;
        }),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe({
        next: (setup: TotpSetupResponse) => {
          this.setup = setup;
        },
        error: (err: any) => {
          this.notificationSvc.error('Error', err.message);
        },
      });
  }

  /** Ends the setup session on the server, then back to login. */
  private finishSetup() {
    this.mfaSetupSvc.logout().subscribe({
      error: (err: any) => console.error('LOGOUT REQUEST ERROR', err),
    });
    this.mfaSetupSvc.clear();
    this.router.navigate(['login']);
  }
}
